import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, getODataRows, resolveODataTotalCount } from './listPageStyles';

const API_BASE = '/api/customeradditionalfields';

// Veri tipleri — backend CustomerAdditionalField.DataType ile eşleşmeli
const DATA_TYPES = [
  { value: 1, label: 'Metin' },
  { value: 2, label: 'Sayı' },
  { value: 3, label: 'Tarih' },
  { value: 4, label: 'Evet/Hayır' },
];
const dataTypeLabel = (v) => DATA_TYPES.find(t => t.value === Number(v))?.label || '-';

const gridActionButton = {
  border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer',
  fontSize: 12, fontWeight: 600, height: 30, lineHeight: '30px', padding: '0 10px', minWidth: 74,
};

const EMPTY_FORM = { textId: '', name: '', dataType: 1, isRequired: false };

export function CustomerAdditionalFieldsPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Name),'${q}'))`;
      }
      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({
        data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders(),
      }));
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterText, pageSize, sortDir, sortField]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => { setFilterText(filterInput); setCurrentPage(1); };
  const handleFilterKeyDown = (event) => { if (event.key === 'Enter') handleFilterSearch(); };
  const clearFilter = () => { setFilterText(''); setFilterInput(''); setCurrentPage(1); };

  const openNew = () => { setForm(EMPTY_FORM); setSelected(null); setModal('new'); };

  const rowToForm = (row) => ({
    textId: row.textId || '',
    name: row.name || '',
    dataType: row.dataType ?? 1,
    isRequired: !!row.isRequired,
  });

  const openEdit = useCallback((row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('edit');
  }, [selected]);

  const openView = useCallback((row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('view');
  }, [selected]);

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    if (!form.textId.trim()) { showToast('error', 'Kod bos olamaz'); return; }
    if (!form.name.trim()) { showToast('error', 'Ad bos olamaz'); return; }

    const payload = {
      textId: form.textId,
      name: form.name,
      dataType: Number(form.dataType),
      isRequired: !!form.isRequired,
    };

    try {
      if (modal === 'new') {
        const res = await fetch(`${API_BASE}/Add`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayit eklenemedi');
        showToast('success', 'Ozellestirilmis alan basariyla eklendi');
      } else {
        const res = await fetch(`${API_BASE}/Update`, {
          method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id, ...payload }),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayit guncellenemedi');
        showToast('success', 'Ozellestirilmis alan basariyla guncellendi');
      }
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayit silinemedi');
      showToast('success', 'Ozellestirilmis alan basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const columns = useMemo(() => [
    { field: 'textId', headerName: 'Kod', flex: 1, minWidth: 140, sortable: true },
    { field: 'name', headerName: 'Ad', flex: 1.4, minWidth: 200, sortable: true },
    { field: 'dataType', headerName: 'Veri Tipi', width: 140, sortable: true, renderCell: (params) => dataTypeLabel(params.row.dataType) },
    { field: 'isRequired', headerName: 'Zorunlu', width: 110, sortable: true, renderCell: (params) => params.row.isRequired ? 'Evet' : 'Hayır' },
    {
      field: 'createdDate', headerName: 'Olusturma Tarihi', width: 170, sortable: true,
      renderCell: (params) => params.row.createdDate ? new Date(params.row.createdDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'updatedDate', headerName: 'Guncelleme Tarihi', width: 180, sortable: true,
      renderCell: (params) => params.row.updatedDate ? new Date(params.row.updatedDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'actions', headerName: 'Islemler', width: 260, sortable: false, filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Goruntule</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Degistir</button>
          <button
            style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }}
            onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], [openEdit, openView]);

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Müşteri Özelleştirilmiş Alan</h2>
          <p style={s.pageSubtitle}>Müşteriler için özel alan tanımlarını oluşturun, düzenleyin ve yönetin.</p>
        </div>
        <button style={{ ...s.btnPrimary, background: 'var(--success)' }} onClick={openNew}>+ Yeni Özelleştirilmiş Alan</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Kod veya ad ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={handleFilterKeyDown}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="musteri-ozellestirilmis-alanlar"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Özelleştirilmiş Alan' : modal === 'edit' ? 'Özelleştirilmiş Alan Düzenle' : 'Özelleştirilmiş Alan Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod *</label>
                  <input
                    style={inputStyle}
                    value={form.textId}
                    onChange={e => setForm(current => ({ ...current, textId: e.target.value }))}
                    readOnly={isView}
                    placeholder="Kod giriniz"
                    autoFocus
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ad *</label>
                  <input
                    style={inputStyle}
                    value={form.name}
                    onChange={e => setForm(current => ({ ...current, name: e.target.value }))}
                    readOnly={isView}
                    placeholder="Ad giriniz"
                  />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Veri Tipi</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={dataTypeLabel(form.dataType)} />
                  ) : (
                    <select style={selectStyle} value={form.dataType} onChange={e => setForm(current => ({ ...current, dataType: Number(e.target.value) }))}>
                      {DATA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Zorunlu</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={form.isRequired ? 'Evet' : 'Hayır'} />
                  ) : (
                    <select style={selectStyle} value={form.isRequired ? '1' : '0'} onChange={e => setForm(current => ({ ...current, isRequired: e.target.value === '1' }))}>
                      <option value="0">Hayır</option>
                      <option value="1">Evet</option>
                    </select>
                  )}
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.textId}"</strong> kodlu özelleştirilmiş alanı silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
